// ============================================================
// Procurement module manifest — R3 retrofit (2026-04-24).
//
// Bundles 4 entities into ONE module (matches Sales' pattern of
// 5 routes per single module). Per ARCHITECTURE.md §4 every
// industry pack lists 'procurement' as a single string — this
// manifest is the implementation of that contract.
//
// Routes contributed:
//   - requisitions       (PR)
//   - purchase-orders    (PO)
//   - grn                (Goods Receipt Notes)
//   - supplier-invoices  (3-way matched supplier bills)
//
// Dependencies:
//   - inventory   (GRN posting calls commitStockMovement)
//   - suppliers   (PO modal needs supplier picker + supplieritems)
// Registry's dependency validator enforces both load before procurement.
// ============================================================

export default {
  id:          'procurement',
  name:        'Procurement',
  version:     '1.0.0',
  description: 'Requisition → PO → GRN → supplier invoice (3-way match) lifecycle.',

  load: () => import('./index.js'),

  // 2026-05-04 sidebar-leak fix — every procure route gates on `procurement.read`.
  // Roles that get it: director + admin (via '*'/__ALL_EXCEPT_TENANT_CONFIG__),
  // production-supervisor (explicit). Staff/shop-floor/sales do NOT see Procure.
  // Per-action permissions (procurement.po.write, procurement.grn.post, etc.)
  // are enforced inside the page — this is sidebar visibility only.
  routes: [
    {
      id:       'requisitions',
      label:    'Requisitions',
      icon:     '📝',
      section:  'procure',
      sidebarPosition: 10,
      visible:  (role, hasPerm) => hasPerm('procurement.read'),
      mount:    'mountRequisitions',
      unmount:  'unmountRequisitions'
    },
    {
      id:       'purchase-orders',
      label:    'Purchase Orders',
      icon:     '📦',
      section:  'procure',
      sidebarPosition: 20,
      visible:  (role, hasPerm) => hasPerm('procurement.read'),
      mount:    'mountPurchaseOrders',
      unmount:  'unmountPurchaseOrders'
    },
    {
      id:       'grn',
      label:    'Goods Received',
      icon:     '📥',
      section:  'procure',
      sidebarPosition: 30,
      visible:  (role, hasPerm) => hasPerm('procurement.read'),
      mount:    'mountGRNs',
      unmount:  'unmountGRNs'
    },
    {
      id:       'supplier-invoices',
      label:    'Supplier Invoices',
      icon:     '🧾',
      section:  'procure',
      sidebarPosition: 40,
      visible:  (role, hasPerm) => hasPerm('procurement.read'),
      mount:    'mountInvoices',
      unmount:  'unmountInvoices'
    }
  ],

  // Firestore collections this module reads/writes.
  schemas: [
    'purchaserequisitions',
    'purchaseorders',
    'goodsreceiptnotes',
    'supplierinvoices'
  ],

  permissions: [
    'procurement.read',
    'procurement.requisition.write',
    'procurement.requisition.approve',
    'procurement.po.write',
    'procurement.po.confirm',
    'procurement.grn.write',
    'procurement.grn.post',       // posting GRN commits StockMovements (inventory.move)
    'procurement.invoice.write',
    'procurement.invoice.approve'
  ],

  // Procurement reads inventory for line picker + writes stock movements
  // on GRN post; reads suppliers for the PO supplier picker + supplier items.
  dependencies: ['inventory', 'suppliers'],

  fitsIndustries: ['manufacturing', 'trading', 'retail'],

  // Cloud Function for invoice PDF parsing (Phase 4 — already deployed).
  functions: ['parseSupplierInvoicePdf'],

  // Dashboard widgets contributed (R3 widget composition).
  dashboardWidgets: [
    {
      id:           'procurement-open-pos-kpi',
      label:        'Open POs',
      section:      'kpi',
      order:        60,
      defaultRoles: ['director', 'admin', 'manager', 'inventory']
    },
    {
      id:           'procurement-pending-grns-kpi',
      label:        'Pending GRNs',
      section:      'kpi',
      order:        70,
      defaultRoles: ['director', 'admin', 'manager', 'inventory']
    },
    {
      id:           'procurement-three-way-mismatched-kpi',
      label:        '3-way mismatched',
      section:      'kpi',
      order:        80,
      defaultRoles: ['director', 'admin', 'manager']
    },
    {
      id:           'procurement-pending-invoices-kpi',
      label:        'Pending invoices',
      section:      'kpi',
      order:        85,
      defaultRoles: ['director', 'admin', 'manager']
    },
    {
      id:           'procurement-month-spend-kpi',
      label:        'This month spend',
      section:      'kpi',
      order:        90,
      defaultRoles: ['director', 'admin', 'manager']
    },
    {
      id:           'procurement-open-pos-list',
      label:        'Open POs needing receipt',
      section:      'left-col',
      order:        20,
      defaultRoles: ['director', 'admin', 'manager', 'inventory']
    },
    {
      id:           'procurement-flagged-invoices-list',
      label:        'Invoices needing review',
      section:      'right-col',
      order:        20,
      defaultRoles: ['director', 'admin', 'manager']
    }
  ]
};
